import { v2Api } from './v2Client';

const LOG = '[SquashAPI payments]';

/**
 * Paiements en ligne (voir Controllers/PaymentController.cs et docs/PAYMENTS.md côté API) — staff uniquement.
 */

/** GET /Payment/transactions — liste paginée + totaux (encaissé, remboursé, anomalies). */
export async function listPaymentTransactions({ status, playerId, from, to, attentionOnly, search, page = 1, pageSize = 50 } = {}) {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (playerId) params.player_id = playerId;
  if (from) params.from = from;
  if (to) params.to = to;
  if (attentionOnly) params.attention_only = true;
  if (search) params.search = search;
  try {
    const { data } = await v2Api.get('/Payment/transactions', { params });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: data?.total ?? 0,
      page: data?.page ?? page,
      pageSize: data?.page_size ?? pageSize,
      summary: data?.summary ?? { succeeded_count: 0, collected_cents: 0, refunded_cents: 0, attention_count: 0 },
    };
  } catch (err) {
    console.error(LOG, 'transactions échoué', { params, err });
    throw err;
  }
}

/** GET /Payment/transactions/{id} — transaction + historique complet. */
export async function getPaymentTransaction(id) {
  const { data } = await v2Api.get(`/Payment/transactions/${encodeURIComponent(id)}`);
  return { transaction: data?.transaction ?? null, events: Array.isArray(data?.events) ? data.events : [] };
}

/** GET /Payment/events — historique paginé et filtrable. */
export async function listPaymentEvents({ transactionId, reservationId, severity, eventType, from, to, page = 1, pageSize = 100 } = {}) {
  const params = { page, page_size: pageSize };
  if (transactionId) params.transaction_id = transactionId;
  if (reservationId) params.reservation_id = reservationId;
  if (severity) params.severity = severity;
  if (eventType) params.event_type = eventType;
  if (from) params.from = from;
  if (to) params.to = to;
  try {
    const { data } = await v2Api.get('/Payment/events', { params });
    return { items: Array.isArray(data?.items) ? data.items : [], total: data?.total ?? 0 };
  } catch (err) {
    console.error(LOG, 'events échoué', { params, err });
    throw err;
  }
}

/** GET /Payment/reservation/{id} — paiement + historique d'une réservation (dialog Agenda). */
export async function getReservationPayment(reservationId) {
  const { data } = await v2Api.get(`/Payment/reservation/${encodeURIComponent(reservationId)}`);
  return {
    reservationId: data?.reservation_id ?? reservationId,
    paymentStatus: data?.payment_status ?? 'not_required',
    transaction: data?.transaction ?? null,
    // Part réglée par le wallet du joueur : { paid_cents, refunded_cents, refundable_cents, entries }.
    wallet: data?.wallet ?? null,
    events: Array.isArray(data?.events) ? data.events : [],
  };
}

/** POST /Payment/refund — intégral (amountCents absent) ou partiel. method : 'card' (Stripe) ou 'wallet'
 *  (part payée par le crédit, recréditée sur le wallet du joueur). */
export async function refundReservationPayment(reservationId, { amountCents, reason, method } = {}) {
  const payload = { reservation_id: String(reservationId) };
  if (amountCents != null) payload.amount_cents = amountCents;
  if (reason) payload.reason = reason;
  if (method) payload.method = method;
  try {
    const { data } = await v2Api.post('/Payment/refund', payload);
    return data;
  } catch (err) {
    console.error(LOG, 'refund échoué', { payload, err });
    throw err;
  }
}

/** POST /Payment/reconcile — resynchronise immédiatement une transaction avec Stripe. */
export async function reconcilePaymentTransaction(transactionId) {
  try {
    const { data } = await v2Api.post('/Payment/reconcile', { transaction_id: String(transactionId) });
    return data;
  } catch (err) {
    console.error(LOG, 'reconcile échoué', { transactionId, err });
    throw err;
  }
}

export const paymentsService = {
  listTransactions: listPaymentTransactions,
  getTransaction: getPaymentTransaction,
  listEvents: listPaymentEvents,
  getReservationPayment,
  refund: refundReservationPayment,
  reconcile: reconcilePaymentTransaction,
};
